import React, { useState, useEffect } from "react"
import { Link } from "gatsby"
import { useCookies } from "react-cookie"
import tw, { styled } from "twin.macro"
import moment from "moment"
import { useLocation } from "@reach/router"

import { brandRules } from "utils/brand"
import { insightsDate } from "utils/insights"
import Divider from "components/divider"
import YouTube from "components/youtube"
import BlockedContent from "components/blocked-content"
import Demand360Ad from "components/demand360-ad"
import Tag from "components/tag"
import Unlocked from "images/icons/unlocked.png"
import ShareButtons from "components/share-buttons"
import OtherInsights from "components/sections/other-insights"
import { sendViewEvent } from "utils/algolia"
import { addIdAttributes } from "utils/content"
import Modal from "components/modal"
import NewsletterForm from "components/sections/newsletter-form"

const Header = styled.div`
  ${tw`pt-20 m:pt-32 flex flex-wrap`}
  background: linear-gradient(337.38deg, #2b1143 0%, #000000 100%);

  h1 {
    ${tw`py-2 pb-8 w-full font-display font-bold text-white`}
    font-size: 30px;
    line-height: 35px;
    @media (min-width: 640px) {
      font-size: 45px;
      line-height: 62px;
    }
  }

  .published {
    ${tw`font-light text-white`}
    font-size: 13px;
    line-height: 12px;
    @media (min-width: 1024px) {
      font-size: 19px;
      line-height: 17px;
    }
  }
`

const Frame = styled.div`
  position: relative;

  &.shared {
    background-attachment: fixed;
    background-image: linear-gradient(180deg, #24c571 35%, #179346 100%);
    padding-left: 8px;
    padding-right: 8px;
  }

  .unlocked-icon {
    border-top: 8px solid #24c571;
    margin-left: -8px;
    margin-right: -8px;
    height: 0;
    padding-bottom: 48px;
    position: sticky;
    top: 50px;
    width: calc(100% + 16px);
    z-index: 10;
    @media (min-width: 720px) {
      padding-bottom: 98px;
      top: 82px;
    }

    img,
    picture {
      position: absolute;
      top: -1px;
      right: 8px;
      @media (max-width: 720px) {
        height: 50px;
        width: 50px;
      }
    }
  }

  .frame-bottom {
    background-color: #179346;
    position: sticky;
    bottom: 0;
    height: 8px;
    margin-left: -8px;
    margin-right: -8px;
    width: calc(100% + 16px);
  }
`

export const Main = styled.div`
  ${tw`w-full`}
  img {
    padding-top: 1rem;
    padding-bottom: 1rem;
    ${tw`w-screen mx-auto`}
  }
`

const Content = styled.div`
  ${tw`font-body text-base text-grey-10`}
  font-size: 15px;
  line-height: 30px;
  @media (min-width: 640px) {
    font-size: 17px;
    line-height: 34px;
  }

  p,
  li {
    ${tw`mb-4`}
  }

  p.blurred {
    padding-top: 20px;
    color: transparent;
    text-shadow: 0 0 13px rgba(0, 0, 0, 0.5);
    user-select: none;
  }

  a {
    ${tw`text-dodgeblue`}
    &:hover {
      text-decoration: underline;
    }
  }

  ol {
    ${tw`pl-8 list-decimal`}
  }

  ul {
    ${tw`pl-8 list-disc`}
  }

  h1,
  h2,
  h3,
  h4,
  h5 {
    ${tw`font-display text-grey-15`}
    display: block;
    margin-left: 0px;
    margin-right: 0px;
  }

  h1,
  h5 {
    margin-bottom: 32px;
    margin-top: 80px;

    font-size: 21px;
    line-height: 28px;
    @media (min-width: 640px) {
      font-size: 26px;
      line-height: 32px;
    }
  }

  h2 {
    margin-bottom: 21px;
    margin-top: 60px;

    font-size: 19px;
    line-height: 28px;
    @media (min-width: 640px) {
      font-size: 22px;
      line-height: 32px;
    }
  }

  h3 {
    margin-bottom: 10px;
    margin-top: 40px;

    font-size: 17px;
    line-height: 28px;
    @media (min-width: 640px) {
      font-size: 19px;
      line-height: 32px;
    }
  }

  h4 {
    margin-bottom: 0px;
    margin-top: 0px;

    font-size: 15px;
    line-height: 30px;
    @media (min-width: 640px) {
      font-size: 17px;
      line-height: 34px;
    }
  }

  b,
  strong {
    ${tw`font-display`}
  }

  img,
  img.size-full {
    width: 100%;
  }

  img[width] {
    width: auto;
    margin-left: unset;
    margin-right: unset;
  }

  .tags {
    ${tw`font-display font-bold text-grey-45`}
    font-size: 13px;
    letter-spacing: 0;
    line-height: 17px;
    text-transform: uppercase;
    white-space: normal;
    @media (min-width: 640px) {
      font-size: 19px;
    }

    .tags-label {
      height: 30px;
      display: inline-block;
      padding-right: 0.5rem;
      padding-top: 2px;
      vertical-align: bottom;
    }
  }
`

const TagStyle = styled.span`
  ${tw`bg-grey-95 mx-1 font-display font-bold text-grey-20`}
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 40px;
  border-radius: 2.8px;
  padding: 8px;
  text-transform: uppercase;
  white-space: nowrap;
  @media (min-width: 640px) {
    font-size: 14.56px;
  }
`

const InsightsTag = ({ children }) => <TagStyle>{children}</TagStyle>

const tagLink = (tag) =>
  `/insights/?refinementList[${tag.alias || tag.type}][]=${tag.name}`

const getTags = (post) => {
  const tagConfig = [
    {
      type: "titlesJson",
      name: "name",
      key: "id",
      alias: "shows",
    },
    {
      type: "moviesJson",
      name: "name",
      key: "id",
      alias: "movies",
    },
    {
      type: "talentJson",
      name: "name",
      key: "id",
      alias: "talent",
    },
    {
      type: "platforms",
      name: "name",
      key: "slug",
    },
    {
      type: "metrics",
      name: "name",
    },
    {
      type: "genres",
      name: "name",
      key: "slug",
    },
    {
      type: "industries",
      name: "title",
      key: "slug",
    },
    {
      type: "applications",
      name: "title",
      key: "slug",
      alias: "solutions",
    },
  ]
  let tags = []
  tagConfig.forEach((tagType) => {
    if (post[tagType.type] && typeof post[tagType.type] === "object") {
      const filteredTags =
        post[tagType.type].map((tag) => ({
          type: tagType.type,
          name: tag[tagType.name],
          key: tagType.key ? tag[tagType.key] : null,
          alias: tagType.alias || null,
        })) || []
      tags = tags.concat(filteredTags)
    }
  })
  return tags.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
}

const Post = ({ post, shared = false }) => {
  const coverImage = post.featuredImage?.url ? true : false
  const imageURL = post.featuredImage?.url
  const imageHandle = post.featuredImage?.handle
  const [cookies, setCookie] = useCookies(["article"])

  const [modal, setModal] = useState(false)
  const location = useLocation()

  const category = post.insightsCategories?.length
    ? post.insightsCategories[0]
    : false

  // Premium posts older than 6 months are open to everyone
  const expireAt = moment().subtract(6, "months")
  if (
    post.publishingDate < expireAt.toISOString() &&
    post.body &&
    post.body.text !== null &&
    post.body.text.trim() !== ""
  ) {
    post.premium = false
  }

  const rawContent =
    post.premium && !shared
      ? post.wordpressId
        ? post.legacySummary
        : post.summary?.html || ""
      : post.wordpressId && post.legacyBody
      ? post.legacyBody || ""
      : post.body?.html
      ? post.body?.html
      : post.summary?.html || ""

  const content = addIdAttributes(rawContent)

  const externalUrl = post.metadata?.external_url
    ? post.metadata.external_url
    : post.externalUrl

  const publisherName =
    externalUrl && post.metadata?.publication_name
      ? post.metadata.publication_name
      : post.articlePublisher?.publisher
      ? post.articlePublisher.publisher
      : null

  const video = post.format === "Video"

  const url = post.relativePath
    ? `${process.env.GATSBY_BASE_URL}${post.relativePath}`
    : false
  const tags = getTags(post)
  const showTags = tags && tags.length > 0
  const showShare = !!url && (!post.premium || shared)
  const mediaBase = process.env.GATSBY_ASSETS_BASE_URL

  useEffect(() => {
    if (post.premium) {
      setCookie("article", post.id, { path: "/" })
    }
  }, [setCookie])

  useEffect(() => {
    sendViewEvent({
      index: process.env.GATSBY_ALGOLIA_INDEX_NAME,
      event: "Article View",
      hitInfo: {
        objectId: post.id,
      },
    })
  }, [])

  useEffect(() => {
    // Open modal if #play
    if (location?.hash === "#play") {
      setModal(true)
    }
  }, [location])

  return (
    <>
      <Header>
        <div
          className={`px-2 m:pl-10 pb-10 w-full text-center m:text-left flex flex-col justify-center ${
            coverImage ? "m:w-1/2" : ""
          }`}
        >
          {!!category && (
            <div>
              {category.slug !== "announcements" && (
                <Link to={`/insights/category/${category.slug}`}>
                  <Tag className="medium">{category.name}</Tag>
                </Link>
              )}
              {category.slug === "announcements" && (
                <Tag className="medium">{category.name}</Tag>
              )}
            </div>
          )}
          <h1 dangerouslySetInnerHTML={{ __html: brandRules(post.title) }}></h1>
          <div className="published">
            {insightsDate(post.publishingDate, true)}
          </div>
        </div>
        {coverImage && !video && (
          <div
            className={`px-3 m:px-0 m:mb-0 w-full m:w-1/2 flex flex-col justify-end ${
              shared ? "" : "-mb-16"
            }`}
          >
            {imageHandle && (
              <picture
                className="w-full h-full object-cover"
                style={{ zIndex: 1 }}
              >
                <source
                  srcSet={`${mediaBase}auto_image/resize=width:640/${imageHandle}, ${mediaBase}auto_image/resize=width:1280/${imageHandle} 2x`}
                  media="(max-width: 1279px)"
                />
                <source
                  srcSet={`${mediaBase}auto_image/resize=width:960/${imageHandle}, ${mediaBase}auto_image/resize=width:1920/${imageHandle} 2x`}
                  media="(min-width: 1280px) and (max-width: 1919px)"
                />
                <source
                  srcSet={`${mediaBase}auto_image/resize=width:1280/${imageHandle}, ${mediaBase}auto_image/resize=width:2560/${imageHandle} 2x`}
                  media="(min-width: 1920px)"
                />
                <img alt={post.featuredImage.altText} src={imageURL} />
              </picture>
            )}
            {!imageHandle && (
              <img
                alt={post.featuredImage.altText}
                className="w-full h-full object-cover"
                loading="lazy"
                src={imageURL}
                style={{ zIndex: 1 }}
              />
            )}
          </div>
        )}
        {!!video && (
          <>
            <div className="px-3 m:px-0 mb-0 w-full m:w-1/2 flex flex-col justify-end z-10">
              <YouTube videoUrl={externalUrl} />
            </div>
            {modal && (
              <Modal isVisible={modal} onClose={() => setModal(false)}>
                <div className="video-player h-full w-full m:px-16">
                  <YouTube
                    videoUrl={externalUrl}
                    active={true}
                    autoplay={true}
                    fixAspectRatio={false}
                  />
                </div>
              </Modal>
            )}
          </>
        )}
      </Header>
      {!shared && <Divider />}
      {!video && (
        <Frame className={shared ? "shared" : ""}>
          <div className="bg-white">
            {shared && (
              <div className="unlocked-icon">
                <img src={Unlocked} height={70} width={70} alt="" />
              </div>
            )}
            <div
              className={`${
                coverImage && !shared ? "pt-32" : ""
              } py-10 pb-4 l:pt-10 max-w-s mx-auto m:max-w-m l:max-w-l flex flex-row`}
            >
              <div className="flex-shrink-0 w-1/6 hidden l:block"></div>
              <Main id="content">
                <Content
                  dangerouslySetInnerHTML={{
                    __html: `<div>${brandRules(content)}</div>`,
                  }}
                />
                {externalUrl && (
                  <Content>
                    <p>
                      <i>
                        Visit{" "}
                        <a
                          href={externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {publisherName || "the publisher's website"}
                        </a>{" "}
                        to read this article.
                      </i>
                    </p>
                  </Content>
                )}
                {showShare && (
                  <>
                    <hr className="light max-w-s mx-auto m:max-w-m l:max-w-l my-10" />
                    <ShareButtons url={url} title={brandRules(post.title)} />
                  </>
                )}
              </Main>
              <div className="flex-shrink-0 w-1/6 hidden l:block"></div>
            </div>
            {(!!post.premium || shared) && <BlockedContent shared={shared} />}

            <div className="mt-16">
              <NewsletterForm slanted={false} />
            </div>

            <OtherInsights postId={post.id} />
            {showTags && (
              <>
                <hr className="light max-w-s mx-auto m:max-w-m l:max-w-l" />
                <div
                  className={`pt-12 pb-24 max-w-s mx-auto m:max-w-m l:max-w-l flex flex-row`}
                >
                  <Main>
                    <Content>
                      <div className="tags text-left">
                        <span className="tags-label">Tags:</span>
                        {tags.map((tag, idx) => (
                          <React.Fragment key={idx}>
                            <Link to={tagLink(tag)}>
                              <InsightsTag>{tag.name}</InsightsTag>
                            </Link>{" "}
                          </React.Fragment>
                        ))}
                      </div>
                    </Content>
                  </Main>
                </div>
              </>
            )}
          </div>
          {!!shared && <div className="frame-bottom" />}
        </Frame>
      )}
      <Demand360Ad />
    </>
  )
}

export default Post
